import { Inspectable } from "@constants/Inspector";
import {
  Flex,
  isEditableElement,
  isMacPlatform,
  PopupMenu,
  Text,
} from "@thenounproject/lingo-core";
import React, { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useRegisteredActions } from "./registry/useRegisteredActions";
import { eventToShortcutKeys, shortcutToKey, type ActionWithShortcut } from "./registry/types";
import DisplayPanel from "./DisplayPanel";
import ActionsMenuSearchBar from "./ActionsMenuSearchBar";
import keyCodes from "@constants/keyCodes";
import { useActionContext } from "./useActionContext";

// Styled component for shortcut badge
const ShortcutBadge = styled(Text).attrs({
  font: "ui.tiny",
  ml: "auto",
})``;

const isMac = isMacPlatform();

type Props = {
  inspectables: Inspectable[];
  position?: { x: string; y: string };
} & Pick<ComponentProps<typeof PopupMenu>, "source" | "open" | "close" | "vPos" | "hPos">;

const ActionsMenu: React.FC<Props> = ({
  inspectables,
  source,
  open,
  close,
  vPos,
  hPos,
  position,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  // Maintain the input text
  const [inputText, setInputText] = useState<string>("");
  const [highlightedAction, setHighlightedAction] = useState<string | null>(null);

  // Build context using shared hook
  const context = useActionContext({ inspectables, userInput: inputText });

  // Use the new registry-based actions hook
  const { actions, allActions } = useRegisteredActions(context);

  // Flatten all actions for shortcut matching
  const shortcutActions = useMemo(() => {
    return allActions.reduce(
      (acc, action) => {
        if (action.shortcut) {
          const actionKey = shortcutToKey(action.shortcut);
          acc[actionKey] = action;
        }
        return acc;
      },
      {} as { string: ActionWithShortcut }
    );
  }, [allActions]);

  const actionHashString = useMemo(() => {
    return actions.map(category => category.actions.map(action => action.id).join(",")).join("|");
  }, [actions]);

  const prompts = useMemo(
    () =>
      actions.reduce((acc, cat) => {
        const acts = cat.actions.map(a => a.label);
        acc.push(...acts);
        return acc;
      }, [] as string[]),
    [actions]
  );

  useEffect(() => {
    if (highlightedAction) {
      // scroll into view
      const actionElement = document.querySelector(`[data-action-id="${highlightedAction}"]`);
      actionElement?.scrollIntoView({ behavior: "auto", block: "nearest" });
    }
  }, [highlightedAction]);

  // Reset highlighted action when actions change
  // We use a hash string to avoid resetting when the actions change due to an action being taken
  // For example if you make a selection, the action needs to update with the new state
  // but we don't want to reset the highlighted action in that case
  useEffect(() => {
    setHighlightedAction(null);
  }, [actionHashString]);

  // Auto-highlight the first action when there's search input
  useEffect(() => {
    if (inputText && actions.length > 0) {
      const firstAction = actions[0]?.actions?.[0];
      if (firstAction) {
        setHighlightedAction(firstAction.id);
      }
    }
  }, [inputText, actions]);

  const performAction = useCallback(
    (action: string | ActionWithShortcut) => {
      let act: ActionWithShortcut | undefined;
      if (typeof action === "string") {
        act = actions.flatMap(category => category.actions).find(a => a.id === highlightedAction);
      } else {
        act = action;
      }
      if (act) {
        void act.perform();
        if (act.clearInputOnSelect) {
          setInputText("");
        }
        if (act.closeOnSelect) {
          close();
        }
      }
    },
    [actions, close, highlightedAction]
  );

  // Focus the input when the user begins typing
  // Or clear the input/close the menu on esc
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      e.stopImmediatePropagation();
      if (e.key === keyCodes.upArrow) {
        e.preventDefault();
        if (actions.length === 0) return;
        // move selection up
        if (highlightedAction) {
          const currentCategoryIdx = actions.findIndex(cat =>
            cat.actions.find(action => action.id === highlightedAction)
          );
          if (currentCategoryIdx >= 0) {
            const currentCategory = actions[currentCategoryIdx];
            const currentActionIdx = currentCategory.actions.findIndex(
              action => action.id === highlightedAction
            );
            if (currentActionIdx > 0) {
              return setHighlightedAction(currentCategory.actions[currentActionIdx - 1].id);
            } else {
              const targetCategoryIdx =
                currentCategoryIdx === 0 ? actions.length - 1 : currentCategoryIdx - 1;
              const targetCategory = actions[targetCategoryIdx];
              return setHighlightedAction(
                targetCategory.actions[targetCategory.actions.length - 1].id
              );
            }
          }
        }
        const targetCategoryIdx = actions.length - 1;
        const targetCategory = actions[targetCategoryIdx];
        setHighlightedAction(targetCategory.actions[targetCategory.actions.length - 1].id);
      } else if (e.key === keyCodes.downArrow) {
        e.preventDefault();
        if (actions.length === 0) return;
        // move selection down
        if (highlightedAction) {
          const currentCategoryIdx = actions.findIndex(cat =>
            cat.actions.find(action => action.id === highlightedAction)
          );
          if (currentCategoryIdx >= 0) {
            const currentCategory = actions[currentCategoryIdx];
            const currentActionIdx = currentCategory.actions.findIndex(
              action => action.id === highlightedAction
            );
            if (currentActionIdx < currentCategory.actions.length - 1) {
              return setHighlightedAction(currentCategory.actions[currentActionIdx + 1].id);
            } else {
              const targetCategoryIdx =
                currentCategoryIdx === actions.length - 1 ? 0 : currentCategoryIdx + 1;
              const targetCategory = actions[targetCategoryIdx];
              return setHighlightedAction(targetCategory.actions[0].id);
            }
          }
        }
        const targetCategory = actions[0];
        setHighlightedAction(targetCategory.actions[0].id);
      } else if (e.key === keyCodes.enter) {
        // Perform the highlighted action on enter
        if (highlightedAction) {
          // Prevent is important here. Without it modals can open and submit immediately
          e.preventDefault();
          performAction(highlightedAction);
        }
      } else if (e.key === keyCodes.escape) {
        e.preventDefault();
        if (inputText) {
          setInputText("");
        } else {
          close();
        }
      } else {
        const activeElement = document.activeElement;

        if (!activeElement || e.metaKey || e.altKey || e.shiftKey) {
          // Check if this key event matches a registered keyboard shortcut
          const { keyBased, codeBased } = eventToShortcutKeys(e);
          const matchedAction: ActionWithShortcut =
            shortcutActions[codeBased] ?? shortcutActions[keyBased];

          if (matchedAction && matchedAction.enabled) {
            e.preventDefault();
            performAction(matchedAction);
            return;
          }
        }

        const isPrintableCharacter = (key: string) => {
          return key.length === 1 && !key.match(/\p{C}/u);
        };
        if (!isPrintableCharacter(e.key)) return;

        // Don't steal focus if user is already typing in an input/textarea (like the color picker hex code input)
        if (isEditableElement(activeElement)) {
          return;
        }

        inputRef.current?.focus();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [actions, close, highlightedAction, inputText, open, performAction, shortcutActions]);

  // Render the actions grouped by category
  function renderActions() {
    return actions.map(category => (
      <PopupMenu.Section key={category.category} title={category.category}>
        {category.actions.map((action, index) => {
          // 1. Don't do anything if shortucts are disbaled or there isn't one
          // 2. We show the shortcut slightly different for mac/windows since windows doens't have nice icons.
          const shortcutProps: Pick<
            ComponentProps<typeof PopupMenu.Item>,
            "subtitle" | "accessory"
          > = isMac
            ? {
                accessory: <ShortcutBadge color="grayDark">{action.shortcutDisplay}</ShortcutBadge>,
              }
            : { subtitle: action.shortcutDisplay };

          return (
            <PopupMenu.Item
              data-action-id={action.id}
              key={index}
              title={action.label}
              image={
                action.image ??
                (action.icon ? { iconId: action.icon, fill: "black", size: 16 } : undefined)
              }
              onClick={() => performAction(action)}
              isActive={highlightedAction === action.id}
              onMouseEnter={() => setHighlightedAction(action.id)}
              onMouseLeave={() => setHighlightedAction(null)}
              {...shortcutProps}
            />
          );
        })}
      </PopupMenu.Section>
    ));
  }

  if (!open) return null;
  const { x: xPos, y: yPos } = position || { x: "0", y: "0" };
  const popupSource = source || "action-menu";
  const canShowDisplayPanel = !inputText && context.capabilities.canEdit;
  return (
    <>
      {position ? (
        <PopupMenu.ContextMenuAnchor xPos={xPos} yPos={yPos} data-popup-source={popupSource} />
      ) : null}
      <PopupMenu
        data-action-menu="true"
        source={popupSource}
        open={open}
        close={close}
        hPos={hPos}
        vPos={vPos}
        maxHeight="50vh"
        width="280px"
        stayOpenOnClick>
        <Flex flexDirection="column" maxHeight="50vh" overflow="hidden">
          <ActionsMenuSearchBar
            ref={inputRef}
            open={open}
            inspectableCount={inspectables.length}
            prompts={prompts}
            value={inputText}
            onChange={setInputText}
          />

          {/* Scrollable content container */}
          <Flex flexDirection="column" overflow="auto" flex="1">
            {canShowDisplayPanel && <DisplayPanel context={context} onClose={close} />}
            {renderActions()}
            {actions.length === 0 ? (
              <Text font="ui.small" color="grayDarker" textAlign="center" p="m">
                No actions
              </Text>
            ) : null}
          </Flex>
        </Flex>
      </PopupMenu>
    </>
  );
};

export default ActionsMenu;
